import { ReactNode, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { signalsAPI } from '../../services/api'
import type { EquityCurvePoint, MeanInterval, RateInterval, SignalTrackRecordSummary } from '../../types'
import ResultsDisclaimer from '../ResultsDisclaimer'
import { DASHBOARD_GLOSSARY } from '../../config/glossary'
import { formatPnlPct } from '../../utils/pnl'
import { MIN_WIN_RATE_SAMPLE, formatWinRate, hasWinRate } from '../../utils/winRate'
import Tooltip from '../Tooltip'
import { paperAvgRBasis, usePaperRecord } from '../trackRecord/paperRecord'
import { IntervalNote } from '../trackRecord/recordUI'
import { RecordToggle, trackRecordHref, useRecordSource } from '../trackRecord/recordSource'
import TerminalPanel from './TerminalPanel'
import { directionOf } from './chartTheme'
import EquityCurveChart, { toCurvePoints } from './EquityCurveChart'

const formatR = (r: number) => `${r > 0 ? '+' : ''}${r.toFixed(2)}R`

const PNL_TEXT = { up: 'text-green-400', down: 'text-red-400', flat: 'text-gray-300' } as const

const StatBlock = ({ label, hint, children, className = '' }: { label: string; hint?: string; children: ReactNode; className?: string }) => (
  <div className={`rounded-md border border-terminal-border bg-terminal-raised/40 p-3 ${className}`}>
    <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
      {hint ? (
        <Tooltip content={hint} className="uppercase">
          {label}
        </Tooltip>
      ) : (
        label
      )}
    </p>
    {children}
  </div>
)

/** The win rate never stands alone: its W/L split and closed total sit beside it. */
const WinRateBlock = ({
  rate,
  closed,
  wins,
  losses,
  breakevens = 0,
  noun,
  interval,
}: {
  rate: number | null | undefined
  closed: number
  wins: number
  losses: number
  breakevens?: number
  noun: string
  interval?: RateInterval | null
}) => (
  <StatBlock label="Win rate" className="col-span-2 lg:col-span-1">
    <p className="mt-1 flex flex-wrap items-baseline gap-x-2 font-mono tabular-nums" data-testid="snapshot-win-rate">
      <span className="text-2xl font-bold text-gray-50">
        {hasWinRate(rate, closed) ? (
          formatWinRate(rate, closed)
        ) : (
          <Tooltip
            content={
              closed > 0
                ? DASHBOARD_GLOSSARY.winRateSmallSample(closed, MIN_WIN_RATE_SAMPLE)
                : DASHBOARD_GLOSSARY.winRateEmpty
            }
          >
            —
          </Tooltip>
        )}
      </span>
      <span className="text-sm text-gray-400">
        · <span className="text-green-400">{wins}W</span>/<span className="text-red-400">{losses}L</span>
        {breakevens > 0 && (
          <>
            /<span className="text-gray-300">{breakevens}BE</span>
          </>
        )}{' '}
        · {closed} {noun}
      </span>
    </p>
    <div
      className="mt-2.5 flex h-1.5 gap-px overflow-hidden rounded-full bg-terminal-border"
      role="img"
      aria-label={`${wins} wins, ${losses} losses${breakevens ? `, ${breakevens} breakeven` : ''}`}
    >
      {wins > 0 && <span className="bg-green-500" style={{ flexGrow: wins }} />}
      {losses > 0 && <span className="bg-red-400" style={{ flexGrow: losses }} />}
      {breakevens > 0 && <span className="bg-slate-500" style={{ flexGrow: breakevens }} />}
    </div>
    {hasWinRate(rate, closed) && (
      <IntervalNote
        interval={interval}
        format={(value) => `${value.toFixed(1)}%`}
        testId="snapshot-win-rate-interval"
        dense
      />
    )}
  </StatBlock>
)

/**
 * ``basis`` is required and names what the figure is: the engine's is before
 * fees, the paper account's after its own simulated fee, and both render here;
 * without the basis the toggle would swap one definition of "Avg R" for
 * another under an unchanged label.
 */
const AvgRBlock = ({
  avgR,
  basis,
  interval,
}: {
  avgR: number | null
  basis: string
  interval?: MeanInterval | null
}) => (
  <StatBlock label="Avg R" hint={DASHBOARD_GLOSSARY.avgR}>
    <p
      className={`mt-1 font-mono text-xl font-bold tabular-nums ${avgR == null ? 'text-gray-500' : PNL_TEXT[directionOf(avgR)]}`}
      data-testid="snapshot-avg-r"
    >
      {avgR == null ? '—' : formatR(avgR)}
    </p>
    <p className="text-[11px] text-gray-500" data-testid="snapshot-avg-r-basis">
      {basis}
    </p>
    <IntervalNote interval={interval} format={formatR} testId="snapshot-avg-r-interval" zeroTest dense />
  </StatBlock>
)

const Progress = ({ label, pct }: { label: string; pct: number }) => (
  <div
    className="mt-2 h-1.5 overflow-hidden rounded-full bg-terminal-border"
    role="progressbar"
    aria-label={label}
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={Number(pct.toFixed(1))}
  >
    <div
      className="h-full rounded-full bg-gradient-to-r from-indigo-500 via-violet-400 to-sky-300 shadow-[0_0_8px_rgba(129,140,248,0.55)]"
      style={{ width: `${Math.min(100, Math.max(pct, 1.5))}%` }}
    />
  </div>
)

const Headline = ({ label, hint, value, caption }: { label: string; hint: string; value: number | null; caption?: string }) => (
  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
    <span className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
      <Tooltip content={hint} className="uppercase">
        {label}
      </Tooltip>
    </span>
    {value != null && (
      <>
        <span
          className={`font-mono text-2xl font-bold tabular-nums ${PNL_TEXT[directionOf(value)]}`}
          data-testid="equity-net"
          data-direction={directionOf(value)}
        >
          {formatPnlPct(value)}
        </span>
        {caption && <span className="text-xs text-gray-500">{caption}</span>}
      </>
    )}
  </div>
)

const EmptyCurve = ({ title, detail }: { title: string; detail: string }) => (
  <div className="flex h-full flex-col items-center justify-center rounded-md border border-dashed border-terminal-border px-4 text-center">
    <p className="text-sm text-gray-300">{title}</p>
    <p className="mt-1 text-xs text-gray-500">{detail}</p>
  </div>
)

/** The signal engine's record, exactly as the dashboard always showed it. */
const EngineSnapshot = ({ summary }: { summary: SignalTrackRecordSummary | null }) => {
  const [curve, setCurve] = useState<EquityCurvePoint[] | null>(null)
  const [failed, setFailed] = useState(false)
  const asOf = summary?.as_of

  // Reloads with the dashboard's summary, so the curve and the counts beside it agree.
  useEffect(() => {
    let active = true
    signalsAPI
      .equityCurve(3650)
      .then((data) => {
        if (!active) return
        setCurve(data)
        setFailed(false)
      })
      .catch((error) => {
        console.error('Equity curve failed to load:', error)
        if (active) setFailed(true)
      })
    return () => {
      active = false
    }
  }, [asOf])

  // Gross, before fees, as on Track Record: nothing a user sees assumes a fee.
  const points = useMemo(() => toCurvePoints(curve ?? []), [curve])
  const total = points.length ? points[points.length - 1].cumulative : null

  const closed = summary?.closed_signals ?? 0
  const wins = summary?.wins ?? 0
  const losses = summary?.losses ?? 0
  const phase2 = summary?.phase2_progress

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_16rem]">
      <div className="min-w-0">
        <Headline
          label="Sum of trade returns"
          hint={DASHBOARD_GLOSSARY.cumulativePnl}
          value={total}
          caption={`before fees · unsized, not account return · over ${points.length - 1} closed signal${
            points.length === 2 ? '' : 's'
          }`}
        />
        <div className="relative mt-3 h-56 md:h-64">
          {curve == null ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              {failed ? 'The equity curve is unavailable right now.' : 'Loading track record…'}
            </div>
          ) : points.length === 0 ? (
            <EmptyCurve title="No closed signals yet" detail="The first close, win or loss, starts the curve." />
          ) : (
            <EquityCurveChart
              points={points}
              ariaLabel={`Equity curve over ${points.length - 1} closed signals: ${formatPnlPct(total ?? 0)} before fees, ${wins} wins, ${losses} losses`}
            />
          )}
        </div>
        <ResultsDisclaimer className="mt-2" />
      </div>

      <div className="grid grid-cols-2 gap-3 self-start lg:grid-cols-1">
        <WinRateBlock
          rate={summary?.win_rate}
          closed={closed}
          wins={wins}
          losses={losses}
          breakevens={summary?.breakevens ?? 0}
          noun="closed"
          interval={summary?.win_rate_interval}
        />
        <AvgRBlock
          // The gross mean: the summary is fetched without a fee.
          avgR={summary?.avg_r_gross ?? null}
          basis="before fees · per close"
        />
        <StatBlock label="Closed" hint={phase2 ? DASHBOARD_GLOSSARY.phase2Progress(phase2.threshold) : undefined}>
          <p className="mt-1 font-mono text-xl font-bold tabular-nums text-gray-50" data-testid="snapshot-closed">
            {summary ? closed : '—'}
          </p>
          {phase2 && (
            <>
              <Progress label="Progress to Phase 2 training" pct={phase2.progress_pct} />
              <p className="mt-1 text-[11px] text-gray-500" data-testid="snapshot-phase2">
                {phase2.reached
                  ? 'Enough closes for Phase 2 to train'
                  : `usable ${phase2.usable_rows} / ${phase2.threshold} toward Phase 2 · ${phase2.remaining} to go`}
              </p>
            </>
          )}
        </StatBlock>
      </div>
    </div>
  )
}

/**
 * The breakout forward test's record from its own ledger: account return net
 * of fees, the daily equity curve, and its closed trades. With nothing closed
 * it says so; it never borrows the signal engine's numbers.
 */
const PaperSnapshot = () => {
  const { record, failed } = usePaperRecord()

  if (!record) {
    return (
      <div className="flex h-56 items-center justify-center text-sm text-gray-500 md:h-64">
        {failed ? 'The paper record is unavailable right now.' : 'Loading paper record…'}
      </div>
    )
  }

  const { account, closed, wins, losses, winRate, avgR, curve } = record
  const notStarted = account.status === 'not_started'
  const inProgress = closed === 0

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_16rem]">
      <div className="min-w-0">
        <Headline
          label="Account return"
          hint={DASHBOARD_GLOSSARY.paperReturn}
          value={notStarted || inProgress ? null : account.profit_loss_pct}
          caption={`net of fees · ${closed} closed trade${closed === 1 ? '' : 's'} · ${account.open_positions} open`}
        />
        <div className="relative mt-3 h-56 md:h-64" data-testid="paper-snapshot-curve">
          {notStarted ? (
            <EmptyCurve
              title="The forward test has not started yet"
              detail="Its first scheduled run opens the pretend $10,000 account. Nothing from the signal engine is shown here."
            />
          ) : inProgress ? (
            <EmptyCurve
              title={`Forward test in progress · day ${account.day} of ~${account.eval_days}`}
              detail={`No trades have closed yet (${account.open_positions} open). The record starts with the first close, win or loss.`}
            />
          ) : curve.length < 2 ? (
            <EmptyCurve title="Not enough history to plot yet" detail="The account is valued once per closed daily candle." />
          ) : (
            <EquityCurveChart
              points={curve}
              mode="days"
              ariaLabel={`Paper account equity over ${account.day} days: ${formatPnlPct(account.profit_loss_pct)} net of fees, ${wins} wins, ${losses} losses`}
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 self-start lg:grid-cols-1">
        <WinRateBlock rate={winRate} closed={closed} wins={wins} losses={losses} noun="closed trades" />
        <AvgRBlock avgR={avgR} basis={paperAvgRBasis(account)} />
        <StatBlock label="Forward test" hint={DASHBOARD_GLOSSARY.paperProgress(account.eval_days, account.kill_min_trades)}>
          <p className="mt-1 font-mono tabular-nums" data-testid="snapshot-paper-day">
            <span className="text-xl font-bold text-gray-50">{notStarted ? 0 : account.day}</span>
            <span className="text-sm text-gray-500">/{account.eval_days} days</span>
          </p>
          <Progress label="Progress through the forward test" pct={notStarted ? 0 : (account.day / account.eval_days) * 100} />
          <p className="mt-1 text-[11px] text-gray-500">
            {notStarted ? 'not started' : account.status === 'running' ? 'in progress' : account.status.replace('_', ' ')}
          </p>
        </StatBlock>
      </div>
    </div>
  )
}

/**
 * The automated track record at a glance, switchable between the signal engine
 * and the live Paper forward test (default: DEFAULT_RECORD_SOURCE, due to flip
 * to Paper before launch). Either way: every
 * close counted, losses at full weight, the win rate beside its W/L split and
 * closed total. Never the manual trade journal, and never the two records
 * mixed.
 */
const TrackRecordSnapshot = ({ summary }: { summary: SignalTrackRecordSummary | null }) => {
  const [source, setSource] = useRecordSource()

  return (
    <TerminalPanel
      title="Track record"
      tone={source === 'paper' ? 'violet' : 'indigo'}
      subtitle={
        <p className="inline-flex items-center gap-1.5 rounded-full border border-terminal-border bg-terminal-bg/60 px-2.5 py-0.5 text-[11px] text-gray-400">
          <svg aria-hidden="true" viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3 text-indigo-300">
            <path
              fillRule="evenodd"
              d="M10 1.94 3.5 4.6v4.9c0 4.02 2.72 7.58 6.5 8.56 3.78-.98 6.5-4.54 6.5-8.56V4.6L10 1.94Zm3.28 6.34a.75.75 0 0 0-1.06-1.06L9 10.44 7.78 9.22a.75.75 0 1 0-1.06 1.06l1.75 1.75a.75.75 0 0 0 1.06 0l3.75-3.75Z"
              clipRule="evenodd"
            />
          </svg>
          automated · tamper-evident · losses shown at full weight
        </p>
      }
      action={
        <div className="flex flex-wrap items-start gap-3">
          <RecordToggle source={source} onChange={setSource} />
          <Link
            to={trackRecordHref(source)}
            className="pt-1.5 text-xs font-semibold text-primary-400 hover:text-primary-300"
          >
            Full record →
          </Link>
        </div>
      }
    >
      {source === 'engine' ? <EngineSnapshot summary={summary} /> : <PaperSnapshot />}
    </TerminalPanel>
  )
}

export default TrackRecordSnapshot
